import { useEffect, useState } from 'react'
import styled from 'styled-components'
import getSkills, { Skill } from '../libs/skill-service'
import StudentBarChart from './student/StudentBarChart'
import { Assessment } from '../libs/assessment-service'
import ReactPlayer from 'react-player'
import { getPresignedVideoUrl } from '../libs/video-service'
import SkillLabel from './SkillLabel'
import { UpdateModal } from './assessment-info/UpdateModal'

const Wrapper = styled.label`
    display: flex;
    flex-direction: column;
    min-height: 600px;
    width: 100%;
`

const SkillWrapper = styled.div`
    display: flex;
    margin: 30px;
`

const AssessmentWrapper = styled.div`
    display: flex;
    margin: 0 30px;
`

const SkillDiagramWrapper = styled.div`
    padding: 30px;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    min-height: 0;
`

const Button = styled.div<{ selected: boolean }>`
    width: 80px;
    display: flex;
    justify-content: center;
    padding: 5px;

    ${({ selected }) =>
        selected &&
        `
    border-bottom: 3px solid #31c690;`}
`
const SelectedAssessmentWrapper = styled.div`
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const VideosWrapper = styled.div`
    display: flex;
    justify-content: center;
`

const UpdateButton = styled.div`
    all: unset;
    cursor: pointer;
    background-color: #115ff5;
    color: white;
    padding: 10px;
    display: flex;
    justify-content: center;
    border-radius: 8px;
    &:hover {
        cursor: pointer;
        background-color: #115ff590;
    }
`

const StudentTableWrapper = styled.div`
    margin-top: 30px;
    border: 1px solid grey;
    border-radius: 10px;
    width: 50%;
    display: flex;
    justify-content: center;
    text-align: center;
`

const BatchSelect = styled.select`
    padding: 8px 14px;
    border: 2px solid #e5e6eb;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    background-color: #ffffff;
    color: #333333;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;

    &:hover,
    &:focus {
        border-color: #1fa474;
    }
`

const Table = styled.table`
    width: 100%;
    padding: 20px;
`
const TR = styled.tr``

const TD = styled.td`
    padding: 5px;
`
interface AssessmentsInfoProps {
    assessments: Assessment[]
    handleUpdateScore: (
        message: string,
        score: number,
        selectedAssessment: Assessment
    ) => void
}

interface Data {
    skill: string
    result: Result[]
}

interface Result {
    batch: string
    score: number
    id: number
}

export default function AssemssmentsInfo({
    assessments,
    handleUpdateScore,
}: AssessmentsInfoProps) {
    const [skills, setSkills] = useState<Skill[]>([])
    const [skill, setSkill] = useState<string>('')
    const [selectedAssessment, setSelectedAssessment] =
        useState<Assessment | null>(assessments[0] || null)
    const [videoUrl, setVideoUrl] = useState<string>('')
    const [outputUrl, setOutputUrl] = useState<string>('')
    const [skillAssessments, setSkillAssessments] = useState<Assessment[]>([])

    // Fetch skills once on mount only
    useEffect(() => {
        const fetchSkills = async () => {
            const { skills } = await getSkills()
            setSkills(skills)
        }
        fetchSkills()
    }, [])

    // When skills or assessments update, pick the best default selection
    useEffect(() => {
        if (skills.length === 0) return
        if (assessments.length === 0) {
            setSelectedAssessment(null)
            setVideoUrl('')
            return
        }
        const firstMatchingSkill =
            skills.find((s) => assessments.some((a) => a.skill === s.name)) ||
            skills[0]
        setSkill(firstMatchingSkill.name)
        const firstAssessment =
            assessments.find((a) => a.skill === firstMatchingSkill.name) || null
        setSelectedAssessment(firstAssessment)
        if (firstAssessment) {
            getPresignedVideoUrl(firstAssessment.id).then(setVideoUrl)
        } else {
            setVideoUrl('')
        }
    }, [skills, assessments])

    async function handleSkillSelect(skillName: string) {
        setSkill(skillName)
        const assessment =
            assessments.find((a) => a.skill === skillName) || null
        setSelectedAssessment(assessment)
        setSkillAssessments(assessments.filter((a) => a.skill === skillName))
        if (assessment) {
            const url = await getPresignedVideoUrl(assessment.id)
            setVideoUrl(url)
        } else {
            setVideoUrl('')
        }
    }

    const dataTest: Data[] = [
        {
            skill: 'Jump',
            result: [],
        },
        {
            skill: 'Catch',
            result: [],
        },
        {
            skill: 'Skip',
            result: [],
        },
        {
            skill: 'Hop',
            result: [],
        },
        {
            skill: 'Sidestep',
            result: [],
        },
        {
            skill: 'Kick',
            result: [],
        },
        {
            skill: 'Throw',
            result: [],
        },
    ]

    assessments.forEach((a) => {
        const el = dataTest.find((dt) => a.skill === dt.skill)
        el?.result.push({
            batch: a.batch,
            score: a.finalScore,
            id: a.id,
        })
    })

    const skillData = dataTest.find((d) => d.skill === skill)

    let data: any

    if (skillData) {
        data = {
            labels: skillData.result.map((r) => r.batch),
            datasets: [
                {
                    label: skillData.skill,
                    data: skillData.result.map((r) => r.score),
                },
            ],
        }
    }

    async function handleBarClick(dataIndex: number) {
        if (!skillData) {
            return
        }
        const assessmentId = skillData.result[dataIndex].id
        const assessment = assessments.find((a) => a.id === assessmentId)

        if (!assessment) {
            return
        }

        setSelectedAssessment(assessment)
        const url = await getPresignedVideoUrl(assessment.id)
        setVideoUrl(url)
    }

    async function handleAssessmentClick(id: number) {
        if (!skillData) {
            return
        }
        const assessment = assessments.find((a) => a.id === id)

        if (!assessment) {
            return
        }

        setSelectedAssessment(assessment)
        const url = await getPresignedVideoUrl(assessment.id)
        setVideoUrl(url)
    }

    return (
        <Wrapper>
            <SkillWrapper>
                {skills.map((s, i) => {
                    return (
                        <Button
                            key={i}
                            onClick={() => handleSkillSelect(s.name)}
                            selected={s.name === skill}
                        >
                            {s.name}
                        </Button>
                    )
                })}
            </SkillWrapper>

            <SkillDiagramWrapper>
                {!selectedAssessment ? (
                    <NoDataWrapper>
                        There is no data for this skill yet. Please record and
                        upload the video to see the analysis.
                    </NoDataWrapper>
                ) : (
                    <SelectedAssessmentWrapper>
                        <VideosWrapper>
                            <ReactPlayer controls url={videoUrl} />
                        </VideosWrapper>
                        <div>
                            <DetailScoreTopLine>
                                <b>Criteria</b>
                                <b>Score</b>
                            </DetailScoreTopLine>
                            {(selectedAssessment.detailScore ?? []).map(
                                (s, i) => {
                                    return (
                                        <DetailScoreLine key={i}>
                                            <div>{s.label}</div>
                                            <DetailScore>
                                                {s.score}
                                                <UpdateButton
                                                    onClick={() =>
                                                        handleUpdateScore(
                                                            s.label,
                                                            s.score,
                                                            selectedAssessment
                                                        )
                                                    }
                                                >
                                                    Update
                                                </UpdateButton>
                                            </DetailScore>
                                        </DetailScoreLine>
                                    )
                                }
                            )}
                            <DetailScoreLine>
                                <b>Overall Skill Score</b>
                                <div>{selectedAssessment.finalScore}</div>
                            </DetailScoreLine>
                            <DetailScoreLine>
                                <b>Skill Standard </b>
                                <SkillLabel
                                    status={selectedAssessment.result}
                                />
                            </DetailScoreLine>
                            <DetailScoreBottomLine />
                        </div>
                    </SelectedAssessmentWrapper>
                )}
            </SkillDiagramWrapper>
        </Wrapper>
    )
}

const DetailScoreTopLine = styled.div`
    border: 1px solid grey;
    border-bottom: none;
    background-color: #fafafa;
    padding: 10px 30px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    display: flex;
    justify-content: space-between;
`

const DetailScoreLine = styled.div`
    border: 1px solid grey;
    border-bottom: none;
    padding: 10px 30px;
    display: flex;
    justify-content: space-between;
`

const DetailScore = styled.div`
    display: flex;
    gap: 20px;
`

const DetailScoreBottomLine = styled.div`
    border: 1px solid grey;
    background-color: #fafafa;
    padding: 15px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
`

const NoDataWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
`
